import type { UseTuningSession } from '../hooks/useTuningSession';
import { Card } from '@/components/ui/Card';
import { GoalCard, TipCard, WarningCard, InfoCard } from '../components/Cards';
import { CheatSheetButton } from '../components/CheatSheets';

interface NockingDLoopData {
  /** Measured nock height above square (inches, + = above, - = below). */
  nockHeightOffset?: number;
  dLoopInstalled?: boolean;
  /** Above the nock (gap between top of arrow and lower D-loop knot). */
  dLoopUpperGap?: number;
  /** Below the nock (gap between bottom of arrow and upper D-loop knot). */
  dLoopLowerGap?: number;

  // ─────────────────────────────────────────────────────────────────────
  // Nock Fit sub-card (v113 — School-of-Nock gap closure)
  //
  // Dudley calls nock fit "the hidden accuracy killer" — too loose and the
  // nock falls off mid-draw; too tight and the bow's letoff energy is wasted
  // pinching the string. Self-test: snap an arrow onto the string by hand,
  // hold the bow horizontal nock-down, the arrow should hang ~3-5 seconds
  // before falling. Falls immediately → too loose. Won't fall at all → too
  // tight.
  // ─────────────────────────────────────────────────────────────────────

  /** Result of the snap-tension self-test. */
  nockSnapTest?: 'loose' | 'good' | 'tight' | 'untested';
  /** Visible serving creep / wear next to the nock? */
  servingCreep?: 'none' | 'mild' | 'severe' | 'unchecked';
  /** Tied nock-sets installed in addition to the D-loop knots? */
  tiedNockSets?: boolean;
}

interface Props { tuning: UseTuningSession }

/**
 * Step 5 — Nocking Point & D-Loop
 *
 * Standard starting position is the nock 1/8" above square (nock-high). This
 * compensates for the natural downward kick of the arrow as it leaves the rest.
 * D-loop should have ~1/16-1/8" gap above and below the nock to prevent
 * pinch but stay snug enough not to slide.
 */
export function NockingDLoopStep({ tuning }: Props) {
  const data = (tuning.session.stepData['nocking_d_loop'] as NockingDLoopData | undefined) ?? {};
  const update = (patch: Partial<NockingDLoopData>) =>
    tuning.updateStepData<NockingDLoopData>('nocking_d_loop', { ...data, ...patch });

  const nockOk = data.nockHeightOffset !== undefined
    && data.nockHeightOffset >= 0.0625 && data.nockHeightOffset <= 0.1875;
  const dLoopOk = data.dLoopUpperGap !== undefined && data.dLoopLowerGap !== undefined
    && data.dLoopUpperGap >= 0.0625 && data.dLoopUpperGap <= 0.125
    && data.dLoopLowerGap >= 0.0625 && data.dLoopLowerGap <= 0.125;

  return (
    <>
      <GoalCard title="What we're trying to achieve">
        Set the nocking point ~1/8" above square (nock-high) so the arrow flies level
        off the rest. Install the D-loop with 1/16-1/8" of breathing room above and below
        the arrow's nock — snug, but not pinching.
      </GoalCard>

      <Card padding="lg">
        <div className="flex items-baseline justify-between mb-3 gap-2 flex-wrap">
          <h3 className="font-semibold">Nocking point height</h3>
          <CheatSheetButton defaultSheet="poi-tuning" label="POI guide" />
        </div>
        <p className="text-[12px] text-zinc-400 mb-4 leading-relaxed">
          Clip a bow square to the string so its arm rests on the launcher. Mark where
          the arrow sits on the rest. The nocking point's bottom should be{' '}
          <strong>1/8" above</strong> that mark (nock-high).
        </p>
        <Field label="How far above square is your nock? (inches, + = above)">
          <input
            type="number"
            step={0.0625}
            value={data.nockHeightOffset ?? ''}
            onChange={(e) => update({ nockHeightOffset: parseFloatOrUndef(e.target.value) })}
            placeholder="0.125 (typical)"
            className={inputClass}
          />
        </Field>
        {data.nockHeightOffset !== undefined && (
          <div className="mt-3">
            {nockOk ? (
              <Status ok>Nock height in the typical 1/16-3/16" range — start here, paper tune will fine-tune.</Status>
            ) : data.nockHeightOffset < 0 ? (
              <Status>Nock is BELOW square — most setups will paper-tune with the nock above. Try 1/8" above.</Status>
            ) : data.nockHeightOffset < 0.0625 ? (
              <Status>Nock is barely above square. Most release shooters land at 1/8" — try moving up slightly.</Status>
            ) : (
              <Status>Nock is more than 3/16" high — unusually high. Common only on certain finger-shot setups.</Status>
            )}
          </div>
        )}
      </Card>

      <Card padding="lg">
        <h3 className="font-semibold mb-2">D-loop install</h3>
        <p className="text-[12px] text-zinc-400 mb-4 leading-relaxed">
          The D-loop wraps the string above AND below the nock. Tie it with 1/16-1/8" of
          gap on each side of the nock — enough that the nock isn't pinched (which causes
          nock travel issues at release), but tight enough not to slide along the string
          during draw.
        </p>

        <label className="flex items-center gap-2 cursor-pointer mb-3">
          <input
            type="checkbox"
            checked={!!data.dLoopInstalled}
            onChange={(e) => update({ dLoopInstalled: e.target.checked })}
            className="w-4 h-4 accent-ember-500"
          />
          <span className="text-sm">D-loop is installed</span>
        </label>

        {data.dLoopInstalled && (
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Gap ABOVE the nock (in)">
              <input
                type="number"
                step={0.0625}
                value={data.dLoopUpperGap ?? ''}
                onChange={(e) => update({ dLoopUpperGap: parseFloatOrUndef(e.target.value) })}
                placeholder="0.0625-0.125"
                className={inputClass}
              />
            </Field>
            <Field label="Gap BELOW the nock (in)">
              <input
                type="number"
                step={0.0625}
                value={data.dLoopLowerGap ?? ''}
                onChange={(e) => update({ dLoopLowerGap: parseFloatOrUndef(e.target.value) })}
                placeholder="0.0625-0.125"
                className={inputClass}
              />
            </Field>
          </div>
        )}

        {data.dLoopInstalled && data.dLoopUpperGap !== undefined && data.dLoopLowerGap !== undefined && (
          <div className="mt-3">
            {dLoopOk ? (
              <Status ok>D-loop gaps look good — proper clearance, no nock pinch.</Status>
            ) : (data.dLoopUpperGap < 0.0625 || data.dLoopLowerGap < 0.0625) ? (
              <Status>D-loop is PINCHING the nock. Causes nock travel issues and inconsistent paper tears. Re-tie with 1/16" minimum gap above and below.</Status>
            ) : (
              <Status>D-loop is too LOOSE — will slide along the string during draw. Re-tie tighter; aim for 1/16-1/8" gap.</Status>
            )}
          </div>
        )}
      </Card>

      {/* ──────────────────────────────────────────────────────────────────
          NOCK FIT CARD (v113 — closes the biggest gap in the SoN audit).
          Dudley dedicates three episodes to nock fit + serving creep —
          we now mirror his self-test, his "tied nock-sets WITH D-loop"
          rule, and his serving-creep inspection.
          ────────────────────────────────────────────────────────────── */}
      <Card padding="lg">
        <h3 className="font-semibold mb-3">Nock fit · the hidden accuracy killer</h3>
        <p className="text-[12px] text-zinc-400 mb-4 leading-relaxed">
          Three checks that catch the biggest "why am I missing for no reason"
          culprits. Run them BEFORE paper-tuning — bad nock fit will give you a paper
          tear you can never tune out.
        </p>

        <Field label="Snap-tension self-test">
          <select
            value={data.nockSnapTest ?? 'untested'}
            onChange={(e) =>
              update({ nockSnapTest: e.target.value as NockingDLoopData['nockSnapTest'] })
            }
            className={inputClass}
          >
            <option value="untested">Not tested yet</option>
            <option value="loose">Falls off / drops immediately</option>
            <option value="good">Holds ~3-5 seconds then falls</option>
            <option value="tight">Won't come off without a tug</option>
          </select>
        </Field>
        <p className="text-[11px] text-zinc-500 mt-1 mb-3 leading-snug">
          With bow horizontal nock-down, snap an arrow onto the string and let go. An
          ideal-fit nock hangs ~3-5 seconds then drops. Too loose = nock falls off on
          the draw. Too tight = wastes the bow's let-off energy pinching the string.
        </p>

        {data.nockSnapTest === 'loose' && (
          <Status>
            Too loose. Swap to a nock with a tighter throat (Beiter, Easton G-pin, or
            re-serve the string with one strand of dental floss to thicken slightly).
          </Status>
        )}
        {data.nockSnapTest === 'tight' && (
          <Status>
            Too tight. Try a different nock brand (Firenock A6 is on the smaller side;
            Bohning F-nock is on the larger), or thin the serving by one strand.
          </Status>
        )}
        {data.nockSnapTest === 'good' && (
          <Status ok>Snap fit is correct. This alone fixes ~30% of mystery groups.</Status>
        )}

        <div className="mt-4">
          <Field label="Serving creep / wear next to the nock">
            <select
              value={data.servingCreep ?? 'unchecked'}
              onChange={(e) =>
                update({ servingCreep: e.target.value as NockingDLoopData['servingCreep'] })
              }
              className={inputClass}
            >
              <option value="unchecked">Not inspected yet</option>
              <option value="none">No visible creep · serving tight</option>
              <option value="mild">Slight wear · still serviceable</option>
              <option value="severe">Visible creep or unraveling · re-serve</option>
            </select>
          </Field>
          <p className="text-[11px] text-zinc-500 mt-1 mb-3 leading-snug">
            Look at the serving on both sides of the nock — any frayed strands, sliding
            wraps, or thinning means the serving has "crept" under shot stress. A creeping
            serving moves the nocking point shot-to-shot — paper tune will never settle.
          </p>

          {data.servingCreep === 'severe' && (
            <Status>
              Re-serve before tuning further. A creeping serving will give you a moving
              nocking point and ruin every group. Bohning's "Halo" or BCY's "3D" servings
              hold up best under D-loop pressure.
            </Status>
          )}
          {data.servingCreep === 'mild' && (
            <Status ok>
              Watch it — re-serve at the first sign of unraveling. Common past 2000 shots.
            </Status>
          )}
        </div>

        <div className="mt-4">
          <Field label="Tied nock-sets installed (ABOVE the D-loop)?">
            <select
              value={
                data.tiedNockSets === undefined ? '' : data.tiedNockSets ? 'yes' : 'no'
              }
              onChange={(e) =>
                update({
                  tiedNockSets: e.target.value === '' ? undefined : e.target.value === 'yes',
                })
              }
              className={inputClass}
            >
              <option value="">Not specified</option>
              <option value="yes">Yes · tied nock-sets above + below D-loop</option>
              <option value="no">No · D-loop knots only</option>
            </select>
          </Field>
          <p className="text-[11px] text-zinc-500 mt-1 mb-3 leading-snug">
            The rule: ALWAYS use tied serving nock-sets WITH the D-loop, not in
            place of it. Two small constrictor knots in serving thread (one above, one
            below the D-loop) prevent the D-loop from sliding even slightly under
            sequential shots. Cheap insurance against drift you'd otherwise blame on form.
          </p>

          {data.tiedNockSets === false && (
            <Status>
              Add tied nock-sets above and below the D-loop. Two minutes of work, locks
              nocking point in place for life of the string.
            </Status>
          )}
          {data.tiedNockSets === true && (
            <Status ok>Tied nock-sets installed — nocking point will not drift.</Status>
          )}
        </div>
      </Card>

      <TipCard title="How to tie a D-loop">
        <ol className="list-decimal list-inside space-y-1 text-zinc-300">
          <li>Use 18-22 lb D-loop material (Bohning, BCY, or equivalent — NOT regular serving thread).</li>
          <li>Cut ~4-5" length. Burn both ends to a tight ball with a lighter.</li>
          <li>Tie the lower knot first: form a constrictor / "U" knot below the nock, slide up to 1/16-1/8" below.</li>
          <li>Form the upper knot: same constrictor / "U" pattern, slide down to 1/16-1/8" above the nock.</li>
          <li>Snug both knots with D-loop pliers (or a sturdy pair of needle-nose).</li>
          <li>Test the loop by tugging on it firmly — it should not slip on the string.</li>
        </ol>
        <p className="text-[11px] text-zinc-400 mt-2">
          A loose D-loop is the #1 cause of "my groups are randomly off" complaints. Snug it.
        </p>
      </TipCard>

      <InfoCard title={`Why 1/8" nock-high?`}>
        <p>
          When the bow fires, the arrow rest drops fractionally faster than the arrow's
          vertical inertia. To compensate, we start the arrow slightly above level — by
          the time the rest is out of the way the arrow is flying parallel to the line of
          sight. 1/8" above square is the historically reliable starting point for modern
          compound bows; paper tune adjusts from there in 1/32" increments.
        </p>
      </InfoCard>

      <WarningCard title="Safety reminder">
        Never shoot a bow with a frayed or burned D-loop — failure under draw can cause
        serious injury. Replace D-loops every 1000 shots or whenever you see fraying.
      </WarningCard>
    </>
  );
}

const inputClass =
  'w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-ember-500';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><label className="text-[11px] text-zinc-500 block mb-1">{label}</label>{children}</div>;
}

function Status({ children, ok }: { children: React.ReactNode; ok?: boolean }) {
  return (
    <div className={`text-sm leading-relaxed p-2.5 rounded border ${ok ? 'border-emerald-700/40 bg-emerald-950/20 text-emerald-300' : 'border-amber-700/40 bg-amber-950/20 text-amber-300'}`}>
      {children}
    </div>
  );
}

function parseFloatOrUndef(s: string): number | undefined {
  if (s === '') return undefined;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : undefined;
}
